import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

export interface CalendarProps {
  mode?: 'single' | 'range';
  selected?: Date | { from?: Date; to?: Date };
  onSelect?: (date: Date | { from?: Date; to?: Date } | undefined) => void;
  month?: Date;
  onMonthChange?: (month: Date) => void;
  className?: string;
}

export const Calendar: React.FC<CalendarProps> = ({
  mode = 'single',
  selected,
  onSelect,
  month: controlledMonth,
  onMonthChange,
  className,
}) => {
  const [internalMonth, setInternalMonth] = React.useState(new Date());
  const month = controlledMonth || internalMonth;

  const handleMonthChange = (newMonth: Date) => {
    if (onMonthChange) {
      onMonthChange(newMonth);
    } else {
      setInternalMonth(newMonth);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(month.getFullYear(), month.getMonth() - 1, 1);
    handleMonthChange(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    handleMonthChange(newMonth);
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(month.getFullYear(), month.getMonth(), day);
    
    if (mode === 'single') {
      onSelect?.(clickedDate);
    } else if (mode === 'range') {
      const range = selected as { from?: Date; to?: Date } | undefined;
      
      if (!range?.from || (range.from && range.to)) {
        // Start new range
        onSelect?.({ from: clickedDate, to: undefined });
      } else if (range.from) {
        // Complete range
        if (clickedDate < range.from) {
          onSelect?.({ from: clickedDate, to: range.from });
        } else {
          onSelect?.({ from: range.from, to: clickedDate });
        }
      }
    }
  };

  const isDateSelected = (day: number) => {
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    
    if (mode === 'single') {
      const selectedDate = selected as Date | undefined;
      return selectedDate && 
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate();
    } else if (mode === 'range') {
      const range = selected as { from?: Date; to?: Date } | undefined;
      if (!range?.from) return false;
      
      const dateTime = date.getTime();
      const fromTime = range.from.getTime();
      const toTime = range.to?.getTime();
      
      if (toTime) {
        return dateTime >= fromTime && dateTime <= toTime;
      } else {
        return dateTime === fromTime;
      }
    }
    return false;
  };

  const isDateInRange = (day: number) => {
    if (mode !== 'range') return false;
    
    const range = selected as { from?: Date; to?: Date } | undefined;
    if (!range?.from || !range?.to) return false;
    
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    const dateTime = date.getTime();
    const fromTime = range.from.getTime();
    const toTime = range.to.getTime();
    
    return dateTime > fromTime && dateTime < toTime;
  };

  const isRangeStart = (day: number) => {
    if (mode !== 'range') return false;
    
    const range = selected as { from?: Date; to?: Date } | undefined;
    if (!range?.from) return false;
    
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    return date.getTime() === range.from.getTime();
  };

  const isRangeEnd = (day: number) => {
    if (mode !== 'range') return false;
    
    const range = selected as { from?: Date; to?: Date } | undefined;
    if (!range?.to) return false;
    
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    return date.getTime() === range.to.getTime();
  };

  const daysInMonth = getDaysInMonth(month);
  const firstDayOfMonth = getFirstDayOfMonth(month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className={cn('p-3', className)}>
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousMonth}
          className="h-7 w-7 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="font-semibold">
          {monthNames[month.getMonth()]} {month.getFullYear()}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNextMonth}
          className="h-7 w-7 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Day Names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-muted-foreground h-8 flex items-center justify-center"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1">
        {emptyDays.map((i) => (
          <div key={`empty-${i}`} className="h-8" />
        ))}
        {days.map((day) => {
          const selected = isDateSelected(day);
          const inRange = isDateInRange(day);
          const rangeStart = isRangeStart(day);
          const rangeEnd = isRangeEnd(day);

          return (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              className={cn(
                'h-8 w-full text-sm rounded-md hover:bg-accent transition-colors',
                selected && 'bg-primary text-primary-foreground hover:bg-primary',
                inRange && 'bg-accent',
                rangeStart && 'rounded-r-none',
                rangeEnd && 'rounded-l-none'
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

